import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Send, Lock, Globe, ShieldCheck, ChevronDown, ChevronUp, Users, BookOpen, Clock, Flag, ChefHat, MapPin, ChevronRight, UserCircle2, CheckCircle2, MessageCircle, Heart, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch } from '../lib/authFetch';
import PageHeader from '../components/PageHeader';
import { BackToTopButton, ContentNavFooter } from '../components/NavigationHelpers';
import { DIET_MEALS } from '../data/dietMeals';
import { imageForMeal } from '../data/mealImages';
import { localizedTitle } from '../data/mealTitleTranslations';
import { useLanguage } from '../contexts/LanguageContext';
import { UserAvatar } from '../lib/avatar';
import useTheme4A from '../components/useTheme4A';

// Match the backend's animal-emoji pool + hash so the same anonymous handle
// always renders the same avatar client-side too (e.g. for pending-request
// cards where we don't call the API). Keep in sync with server.py.
const ANON_AVATAR_POOL = ['🦊','🐻','🐼','🐨','🦁','🐯','🐮','🐷','🐸','🐵','🐔','🐧','🦉','🦆','🦅','🦄','🐝','🐙','🐢','🐍','🦋','🦖','🐳','🐬','🐴','🐰','🐺','🦌','🦈','🦩','🐗','🐘'];
function anonAvatarFor(handle) {
  if (!handle) return '👤';
  let total = 0;
  for (let i = 0; i < handle.length; i++) total += handle.charCodeAt(i);
  return ANON_AVATAR_POOL[total % ANON_AVATAR_POOL.length];
}

// One tile inside the "Recent posters" horizontal strip.
// Extracted so the map() body stays readable and to let us give it its own
// click semantics (send favourite request for strangers, no-op for self,
// unfriend flow lives elsewhere).
function PosterTile({ poster, onRequest }) {
  const anon = poster.anon_avatar || anonAvatarFor(poster.handle);
  const clickable = !poster.is_me && !poster.is_friend;
  const wrapperCls = `flex-shrink-0 flex flex-col items-center w-16 ${clickable ? 'group cursor-pointer' : ''}`;
  const tip = poster.is_me
    ? 'You'
    : poster.is_friend
      ? `${poster.handle} · Favourite`
      : `${poster.handle} · Tap to send favourite request`;
  const inner = poster.is_friend && poster.avatar_reveal ? (
    <UserAvatar
      user={{ avatar: poster.avatar_reveal }}
      size="md"
      className="border-2 border-emerald-400"
    />
  ) : (
    <div className={`w-10 h-10 rounded-full border-2 flex items-center justify-center bg-white ${poster.is_me ? 'border-blue-400' : clickable ? 'border-zinc-200 group-hover:border-zinc-500' : 'border-zinc-200'}`}>
      <span className="text-xl leading-none">{anon}</span>
    </div>
  );
  if (clickable) {
    return (
      <button onClick={onRequest} className={wrapperCls} title={tip} data-testid={`poster-tile-${poster.user_id}`}>
        {inner}
        <span className="text-[10px] text-zinc-700 mt-1 truncate w-full text-center">{poster.handle}</span>
      </button>
    );
  }
  return (
    <div className={wrapperCls} title={tip} data-testid={`poster-tile-${poster.user_id}`}>
      {inner}
      <span className="text-[10px] text-zinc-700 mt-1 truncate w-full text-center">{poster.handle}</span>
    </div>
  );
}

const GLOBAL_ROOM = 'Global';

function timeAgo(iso) {
  if (!iso) return '';
  const now = Date.now();
  const t = new Date(iso).getTime();
  const s = Math.max(1, Math.floor((now - t) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function CookedStrip({ language, onMealClick }) {
  const [items, setItems] = useState([]);
  const mealsById = useMemo(() => Object.fromEntries(DIET_MEALS.map(m => [m.id, m])), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/recovery-room/cooked-feed`);
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setItems(d.items || []);
      } catch (e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!items.length) return null;

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-3 mb-3" data-testid="cooked-feed-strip">
      <div className="flex items-center justify-between mb-2 px-1">
        <div className="flex items-center gap-1.5">
          <ChefHat className="w-3.5 h-3.5 text-amber-600" />
          <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-700">Recently cooked</span>
        </div>
        <span className="text-[10px] text-zinc-400">{items.length} this week</span>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" style={{ scrollbarWidth: 'thin' }}>
        {items.map((it, idx) => {
          const meal = mealsById[it.meal_id];
          if (!meal) return null;
          return (
            <button
              key={`${it.meal_id}-${idx}`}
              onClick={() => onMealClick && onMealClick(meal)}
              className="flex-shrink-0 w-24 text-left group"
              data-testid={`cooked-tile-${it.meal_id}`}
            >
              <div className="w-24 h-24 rounded-lg overflow-hidden bg-zinc-100 mb-1 ring-1 ring-zinc-200 group-hover:ring-amber-400 transition">
                <img
                  src={imageForMeal(meal)}
                  alt={localizedTitle(meal, language)}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.src = meal.image; }}
                />
              </div>
              <div className="text-[10px] font-semibold text-zinc-800 truncate leading-tight">{it.first_name}</div>
              <div className="text-[10px] text-zinc-500 truncate leading-tight">{localizedTitle(meal, language)}</div>
              <div className="text-[9px] text-zinc-400">{timeAgo(it.created_at)}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function HandleGate({ onSaved, onCancel }) {
  const [suggestion, setSuggestion] = useState('');
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/recovery-room-handle`);
        if (r.ok) {
          const d = await r.json();
          setSuggestion(d.suggestion || '');
          // Pre-fill input with existing handle (edit mode) or the suggestion (first-time)
          if (!touched) {
            setValue(d.handle || d.suggestion || '');
          }
        }
      } catch (e) { /* silent */ }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only handle bootstrap

  const save = async () => {
    setError(null);
    setSaving(true);
    try {
      const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/recovery-room-handle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ handle: value })
      });
      const d = await r.json();
      if (!r.ok) {
        setError(d.detail || 'Could not save pseudonym');
      } else {
        toast.success('Pseudonym saved');
        onSaved && onSaved(d.handle);
      }
    } catch (e) {
      setError('Could not reach the server');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="handle-gate-modal">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
            <UserCircle2 className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-base font-bold text-zinc-900 leading-tight">Choose your Recovery Room name</h2>
            <p className="text-[12px] text-zinc-600 mt-1 leading-relaxed">
              Recovery is anonymous. Your full legal name is never shown here. Pick a <strong>pseudonym</strong>: first name + last initial, a nickname or a location all work well.
            </p>
          </div>
        </div>

        <label className="block text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5">
          Your pseudonym
        </label>
        <Input
          value={value}
          onChange={(e) => { setValue(e.target.value); setTouched(true); setError(null); }}
          placeholder={suggestion || 'e.g. GraemeT'}
          maxLength={24}
          autoFocus
          className="text-sm"
          data-testid="handle-input"
        />
        <p className="text-[11px] text-zinc-500 mt-1.5 leading-relaxed">
          2–24 chars · letters, digits, spaces, . _ - only.
        </p>

        <div className="mt-3 grid grid-cols-3 gap-1.5">
          {[suggestion, suggestion ? `${suggestion} Qld` : null, 'Ipswich'].filter(Boolean).map((s, i) => (
            <button
              key={i}
              type="button"
              onClick={() => { setValue(s); setTouched(true); setError(null); }}
              className="text-[11px] px-2 py-1.5 rounded-md border border-zinc-200 bg-zinc-50 hover:bg-zinc-100 text-zinc-700 truncate"
              data-testid={`handle-suggestion-${i}`}
            >
              {s}
            </button>
          ))}
        </div>

        {error && (
          <div className="mt-3 text-[12px] text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="handle-error">
            {error}
          </div>
        )}

        <div className="mt-5 flex gap-2">
          {onCancel && (
            <Button
              variant="outline"
              onClick={onCancel}
              className="flex-1"
              data-testid="handle-cancel-btn"
            >
              Not now
            </Button>
          )}
          <Button
            onClick={save}
            disabled={saving || !value || value.length < 2}
            className="flex-1 bg-zinc-950 text-white hover:bg-zinc-800"
            data-testid="handle-save-btn"
          >
            {saving ? 'Saving…' : (<><CheckCircle2 className="w-4 h-4 mr-1.5" />Use this name</>)}
          </Button>
        </div>

        <p className="text-[10px] text-zinc-400 mt-3 leading-relaxed text-center">
          You can change this any time from Profile.
        </p>
      </div>
    </div>
  );
}

function RoomSwitcher({ currentRoom, defaultCountry, userGender, isLgbtqi, onSelect }) {
  const isGlobal = currentRoom === GLOBAL_ROOM;
  const isWomen = currentRoom === 'Women';
  const isMen = currentRoom === 'Men';
  const isLgbtqiRoom = currentRoom === 'LGBTQI+';
  const isCountry = !isGlobal && !isWomen && !isMen && !isLgbtqiRoom;
  const showWomen = userGender === 'female' || userGender === 'superadmin';
  const showMen = userGender === 'male' || userGender === 'superadmin';
  const showLgbtqi = isLgbtqi || userGender === 'superadmin';
  // Layout: Country + Global always; Women/Men/LGBTQI+ appended only when eligible
  const cols = 2 + (showWomen ? 1 : 0) + (showMen ? 1 : 0) + (showLgbtqi ? 1 : 0);
  const gridClass =
    cols === 5 ? 'grid grid-cols-5 gap-1.5' :
    cols === 4 ? 'grid grid-cols-4 gap-1.5' :
    cols === 3 ? 'grid grid-cols-3 gap-1.5' :
    'grid grid-cols-2 gap-1.5';
  const helpText = isWomen
    ? "Women-only room. Posts are visible only to women in Recovery and to SuperAdmins for moderation."
    : isMen
    ? "Men-only room. Honest, accountable conversation between men. SuperAdmins moderate."
    : isLgbtqiRoom
    ? "LGBTQI+ room. A safe space for members who identify as LGBTQI+. SuperAdmins moderate."
    : isCountry
    ? "You're in your country room: most messages will be in your language. Auto-translation still runs for any that aren't."
    : "Global room is multi-lingual. Messages from other languages are auto-translated for you.";

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-2 mb-3" data-testid="room-switcher">
      <div className={gridClass}>
        <button
          onClick={() => onSelect(defaultCountry || GLOBAL_ROOM)}
          disabled={!defaultCountry || defaultCountry === GLOBAL_ROOM}
          className={`px-2 py-2 rounded-lg text-[11px] font-semibold flex items-center justify-center gap-1 transition ${
            isCountry ? 'bg-zinc-950 text-white' : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
          } ${!defaultCountry || defaultCountry === GLOBAL_ROOM ? 'opacity-40 cursor-not-allowed' : ''}`}
          data-testid="room-tab-country"
        >
          <MapPin className="w-3.5 h-3.5" />
          <span className="truncate">{defaultCountry && defaultCountry !== GLOBAL_ROOM ? defaultCountry : 'Country'}</span>
        </button>
        <button
          onClick={() => onSelect(GLOBAL_ROOM)}
          className={`px-2 py-2 rounded-lg text-[11px] font-semibold flex items-center justify-center gap-1 transition ${
            isGlobal ? 'bg-zinc-950 text-white' : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
          }`}
          data-testid="room-tab-global"
        >
          <Globe className="w-3.5 h-3.5" />
          Global
        </button>
        {showWomen && (
          <button
            onClick={() => onSelect('Women')}
            className={`px-2 py-2 rounded-lg text-[11px] font-semibold flex items-center justify-center gap-1 transition ${
              isWomen ? 'bg-rose-700 text-white' : 'bg-rose-50 text-rose-700 hover:bg-rose-100'
            }`}
            data-testid="room-tab-women"
          >
            <Heart className="w-3.5 h-3.5" />
            Women
          </button>
        )}
        {showMen && (
          <button
            onClick={() => onSelect('Men')}
            className={`px-2 py-2 rounded-lg text-[11px] font-semibold flex items-center justify-center gap-1 transition ${
              isMen ? 'bg-sky-700 text-white' : 'bg-sky-50 text-sky-700 hover:bg-sky-100'
            }`}
            data-testid="room-tab-men"
          >
            <MessageCircle className="w-3.5 h-3.5" />
            Men
          </button>
        )}
        {showLgbtqi && (
          <button
            onClick={() => onSelect('LGBTQI+')}
            className={`px-2 py-2 rounded-lg text-[11px] font-semibold flex items-center justify-center gap-1 transition ${
              isLgbtqiRoom ? 'bg-purple-700 text-white' : 'bg-purple-50 text-purple-700 hover:bg-purple-100'
            }`}
            data-testid="room-tab-lgbtqi"
          >
            <Sparkles className="w-3.5 h-3.5" />
            LGBTQI+
          </button>
        )}
      </div>
      <p className="text-[10px] text-zinc-500 leading-relaxed mt-2 px-1">{helpText}</p>
    </div>
  );
}

function GroupChatPage() {
  useTheme4A();
  const navigate = useNavigate();
  const location = useLocation();
  const initialRoom = location.state?.room || null;
  const { user } = useAuth();
  const { language } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [recentPosters, setRecentPosters] = useState([]);
  const [friends, setFriends] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLocked, setIsLocked] = useState(true);
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [room, setRoom] = useState(initialRoom);              // currently viewing room (may be pre-set via navigate state)
  const [defaultCountry, setDefaultCountry] = useState(null); // user's preferred room (resolved on server)
  const [userGender, setUserGender] = useState(null);  // 'female' | 'male' | 'superadmin' | null
  const [isLgbtqi, setIsLgbtqi] = useState(false);     // true if user opted into LGBTQI+ room
  const [handle, setHandle] = useState(null);          // the user's chosen pseudonym
  const [handleLoaded, setHandleLoaded] = useState(false);
  const [showHandleGate, setShowHandleGate] = useState(false);

  const isSuperAdmin = user?.role === 'superadmin' || user?.role === 'beta_tester';
  const isInTwoWeekWindow = (user?.current_week || -2) <= 0;

  // Step 1: check pseudonym FIRST. Anonymity is required before anything else loads.
  useEffect(() => {
    const canAccess = isSuperAdmin || !isInTwoWeekWindow;
    if (!canAccess) return;
    setIsLocked(false);
    (async () => {
      try {
        const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/recovery-room-handle`);
        if (r.ok) {
          const d = await r.json();
          setHandle(d.handle || null);
          setShowHandleGate(!d.has_handle);
        } else {
          setShowHandleGate(true);
        }
      } catch (e) {
        setShowHandleGate(true);
      } finally {
        setHandleLoaded(true);
      }
    })();
  }, [user, isSuperAdmin, isInTwoWeekWindow]);

  // Step 2: only AFTER handle is confirmed, fetch the user's default room
  useEffect(() => {
    if (!handleLoaded || !handle || isLocked) return;
    (async () => {
      try {
        const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/recovery-room/rooms`);
        if (r.ok) {
          const d = await r.json();
          const userRoom = d.user_default_room || GLOBAL_ROOM;
          setDefaultCountry(userRoom);
          // user_gender: 'female' | 'male' | null. SuperAdmins see both Women + Men rooms for moderation.
          const gender = isSuperAdmin ? 'superadmin' : (d.user_gender || null);
          setUserGender(gender);
          setIsLgbtqi(!!d.user_is_lgbtqi);
          if (!room) setRoom(userRoom);
        } else {
          setRoom(GLOBAL_ROOM);
        }
      } catch (e) {
        setRoom(GLOBAL_ROOM);
      }
    })();
  }, [handle, handleLoaded, isLocked]); // eslint-disable-line react-hooks/exhaustive-deps -- room-picker only re-runs when auth/handle state changes

  // Poll messages whenever room changes — only if handle is set (anonymity gate)
  useEffect(() => {
    if (!room || isLocked || !handle) return;
    fetchMessages();
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, [room, isLocked, handle]); // eslint-disable-line react-hooks/exhaustive-deps -- fetchMessages is a stable closure over room/handle

  const fetchMessages = async () => {
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/group-chat?room=${encodeURIComponent(room || GLOBAL_ROOM)}`;
      const response = await authFetch(url);
      if (response.ok) {
        const data = await response.json();
        // Backend now returns { messages, recent_posters }. Older callers
        // used a bare array — keep a safety fallback so a mid-deploy client
        // never crashes mid-poll.
        if (Array.isArray(data)) {
          setMessages(data);
          setRecentPosters([]);
        } else {
          setMessages(data.messages || []);
          setRecentPosters(data.recent_posters || []);
        }
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  // Load / refresh mutual-favourite friends list + incoming pending requests.
  // Runs on mount and after every accept/decline/request action.
  const fetchFriends = async () => {
    try {
      const [favRes, pendRes] = await Promise.all([
        authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/favourites`),
        authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/favourites/pending`),
      ]);
      if (favRes.ok) setFriends((await favRes.json()).items || []);
      if (pendRes.ok) setPendingRequests((await pendRes.json()).items || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    if (!handle || isLocked) return;
    fetchFriends();
  }, [handle, isLocked]);

  const requestFavourite = async (targetUserId, targetHandle) => {
    try {
      const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/favourites/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target_user_id: targetUserId }),
      });
      if (res.ok) {
        toast.success(`Request sent to ${targetHandle}`);
        fetchFriends();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not send request');
      }
    } catch (e) {
      toast.error('Network error');
    }
  };

  const respondFavourite = async (favId, action) => {
    try {
      const res = await authFetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/chat/favourites/${favId}/${action}`,
        { method: 'POST' }
      );
      if (res.ok) {
        toast.success(action === 'accept' ? 'Favourite accepted' : 'Request declined');
        fetchFriends();
        fetchMessages(); // refresh so their avatar starts revealing
      } else {
        toast.error('Could not update request');
      }
    } catch (e) {
      toast.error('Network error');
    }
  };

  const unfriend = async (favId, targetHandle) => {
    if (!window.confirm(`Remove ${targetHandle} from favourites?`)) return;
    try {
      const res = await authFetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/chat/favourites/${favId}`,
        { method: 'DELETE' }
      );
      if (res.ok) {
        toast.success('Removed from favourites');
        fetchFriends();
        fetchMessages();
      }
    } catch (e) { /* ignore */ }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/group-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: newMessage, room: room || GLOBAL_ROOM })
      });
      if (response.ok) {
        setNewMessage('');
        fetchMessages();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send message');
      }
    } catch (error) {
      toast.error('An error occurred');
    }
  };

  const flagMessage = async (messageId) => {
    try {
      const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/group-chat/${messageId}/flag`, { method: 'POST' });
      if (r.ok) {
        toast.success('Reported. A moderator will review it.');
        fetchMessages();
      }
    } catch (e) { /* silent */ }
  };

  const handleCookedTileClick = (meal) => {
    navigate(`/library?tab=diet&meal=${encodeURIComponent(meal.id)}`);
  };

  if (isLocked) {
    return (
      <div className="min-h-screen bg-zinc-100">
        <PageHeader title="Recovery Room (RR)" />
        <main className="pt-20 px-4 flex items-center justify-center" style={{ minHeight: 'calc(100vh - 80px)' }}>
          <div className="text-center max-w-md" data-testid="group-chat-locked">
            <Lock className="w-16 h-16 text-zinc-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-zinc-950 mb-2">Coming Soon</h2>
            <p className="text-sm text-zinc-600 leading-relaxed">
              The Recovery Room (RR) will be available to all users after the <strong>2 Week Window</strong> assessment period. Focus on completing your Personal History and introductory readings for now.
            </p>
            <p className="text-xs font-medium text-zinc-500 uppercase tracking-wider mt-4">
              Current Week: {user?.current_week || user?.week || 'Assessment'}
            </p>
          </div>
        </main>
      </div>
    );
  }

  // Hard gate: must have a pseudonym before anything in the room renders
  if (showHandleGate || !handle) {
    return (
      <div className="min-h-screen bg-zinc-100">
        <PageHeader title="Recovery Room (RR)" />
        <main className="pt-20 px-4 flex items-center justify-center" style={{ minHeight: 'calc(100vh - 80px)' }}>
          <div className="text-center max-w-md opacity-60">
            <UserCircle2 className="w-16 h-16 text-zinc-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-zinc-950 mb-2">Anonymous by design</h2>
            <p className="text-sm text-zinc-600 leading-relaxed">
              Choose a pseudonym to enter the Recovery Room (RR). Your real name is never shown here.
            </p>
          </div>
        </main>
        <HandleGate
          onSaved={(newHandle) => {
            setHandle(newHandle);
            setShowHandleGate(false);
          }}
          onCancel={() => navigate('/dashboard')}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-100">
      <PageHeader title="Recovery Room (RR)" />
      <BackToTopButton />

      <main className="pt-20 pb-44 px-4">
        <div className="max-w-md mx-auto">
          {/* Cooked feed strip: pinned, lightweight, no moderation needed */}
          <CookedStrip language={language} onMealClick={handleCookedTileClick} />

          {/* Country / Global room switcher */}
          <RoomSwitcher currentRoom={room || GLOBAL_ROOM} defaultCountry={defaultCountry} userGender={userGender} isLgbtqi={isLgbtqi} onSelect={setRoom} />

          {/* LGBTQI+ welcome + community-specific guidelines (visible only inside the LGBTQI+ room) */}
          {room === 'LGBTQI+' && (
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-4 mb-4" data-testid="lgbtqi-welcome-banner">
              <div className="flex items-start gap-2.5 mb-2.5">
                <Sparkles className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-bold text-purple-900 leading-tight">Welcome to the LGBTQI+ Recovery Room</h3>
                  <p className="text-[11px] text-purple-800 leading-relaxed mt-1">
                    A safe, anonymous space for members of the LGBTQI+ community in Recovery.  Whatever your identity, however you arrived here, you belong.
                  </p>
                </div>
              </div>
              <details className="group">
                <summary className="text-[11px] font-bold text-purple-700 cursor-pointer list-none flex items-center gap-1 hover:text-purple-900" data-testid="lgbtqi-guidelines-toggle">
                  <ChevronRight className="w-3 h-3 transition-transform group-open:rotate-90" />
                  Community guidelines for this room
                </summary>
                <div className="mt-2.5 pl-4 space-y-2.5 text-[11px] text-purple-800 leading-relaxed">
                  <p><strong className="font-bold text-purple-900">Respect every identity.</strong>  Use the names and pronouns members share.  Do not out anyone, here or anywhere.</p>
                  <p><strong className="font-bold text-purple-900">Recovery first.</strong>  Lived experience around identity, family, faith and trauma is welcome.  Keep the focus on how it affects your Recovery.</p>
                  <p><strong className="font-bold text-purple-900">No proselytising.</strong>  No conversion talk.  No criticism of another person's identity, transition or relationships.</p>
                  <p><strong className="font-bold text-purple-900">Confidentiality.</strong>  What is shared in this room stays in this room.  Screenshots and quoting outside this room are not permitted.</p>
                  <p><strong className="font-bold text-purple-900">Flag, do not engage.</strong>  If a message crosses a line, tap the flag icon.  Two flags from different members marks it for moderator review.</p>
                </div>
              </details>
            </div>
          )}

          {/* Guidelines Accordion */}
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden mb-4" data-testid="chat-guidelines">
            <button
              onClick={() => setShowGuidelines(!showGuidelines)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-zinc-50 transition-colors"
              data-testid="toggle-guidelines-btn"
            >
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                <span className="text-xs font-semibold text-zinc-800">Recovery Room Guidelines</span>
              </div>
              {showGuidelines ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
            </button>

            {showGuidelines && (
              <div className="border-t border-zinc-100 p-4 space-y-3">
                <div className="flex items-start gap-2.5">
                  <Users className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-bold text-zinc-800">Principles Above Personalities</p>
                    <p className="text-[11px] text-zinc-500 leading-relaxed">Focus on shared Recovery principles, not individual differences. Respect everyone's journey: we are all here for the same reason.</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <ShieldCheck className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-bold text-zinc-800">Stay Positive: No "Neg Raving"</p>
                    <p className="text-[11px] text-zinc-500 leading-relaxed">Share hope, encouragement and constructive experiences. Avoid negativity spirals: if you're struggling, reach out to your AI Sponsor or attend a meeting.</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <Clock className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-bold text-zinc-800">Recommended: 2 Hours Per Day Maximum</p>
                    <p className="text-[11px] text-zinc-500 leading-relaxed">We encourage a healthy balance. If you've been chatting for a while, consider:</p>
                    <ul className="text-[11px] text-zinc-500 mt-1 space-y-0.5 ml-3 list-disc">
                      <li>Attending a meeting</li>
                      <li>Working through your Axiom ARP module material</li>
                      <li>Exploring the Research Library</li>
                    </ul>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <BookOpen className="w-4 h-4 text-violet-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-bold text-zinc-800">Keep the Focus on Recovery</p>
                    <p className="text-[11px] text-zinc-500 leading-relaxed">This room is for Recovery support. Stay on topic and remember that your words can inspire someone who is struggling today.</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <Flag className="w-4 h-4 text-rose-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-bold text-zinc-800">See something off? Flag it.</p>
                    <p className="text-[11px] text-zinc-500 leading-relaxed">Tap the flag icon on any message. Two flags from different members auto-flags it for moderator review.</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <p className="text-xs font-medium text-zinc-500 uppercase tracking-wider text-center mb-4 flex items-center justify-center gap-2 flex-wrap">
            <span>Monitored by Sentinel AI · You're in <span className="text-zinc-700">{room || GLOBAL_ROOM}</span></span>
            <button
              onClick={() => setShowHandleGate(true)}
              className="inline-flex items-center gap-1 text-zinc-500 hover:text-zinc-800 normal-case tracking-normal underline underline-offset-2"
              data-testid="edit-handle-btn"
            >
              <UserCircle2 className="w-3 h-3" /> as <span className="font-semibold">{handle}</span>
            </button>
          </p>

          {/* Pending favourite requests: accept/decline card */}
          {pendingRequests.length > 0 && (
            <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 mb-3" data-testid="pending-favourites-card">
              <p className="text-[11px] font-bold uppercase tracking-wider text-amber-900 mb-2 flex items-center gap-1.5">
                <Heart className="w-3 h-3" />
                Favourite requests · {pendingRequests.length}
              </p>
              <div className="space-y-2">
                {pendingRequests.map((r) => (
                  <div
                    key={r.id}
                    className="flex items-center gap-2 bg-white border border-amber-200 rounded-lg px-2 py-1.5"
                    data-testid={`pending-favourite-${r.id}`}
                  >
                    <span className="text-lg leading-none">{anonAvatarFor(r.from_handle)}</span>
                    <span className="flex-1 text-sm font-semibold text-zinc-800 truncate">{r.from_handle}</span>
                    <button
                      onClick={() => respondFavourite(r.id, 'accept')}
                      className="text-xs px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700"
                      data-testid={`fav-accept-${r.id}`}
                    >
                      Accept
                    </button>
                    <button
                      onClick={() => respondFavourite(r.id, 'decline')}
                      className="text-xs px-2 py-1 rounded bg-white border border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                      data-testid={`fav-decline-${r.id}`}
                    >
                      Decline
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent posters strip: anonymous avatars for the last 7 unique
              posters. Tap a stranger to send a favourite request; tap a friend
              to unfriend. Own row is inert. */}
          {recentPosters.length > 0 && (
            <div className="bg-white border border-zinc-200 rounded-xl p-3 mb-3" data-testid="recent-posters-strip">
              <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-700 mb-2 flex items-center gap-1.5">
                <Users className="w-3 h-3" />
                Recent posters
              </p>
              <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                {recentPosters.map((p) => (
                  <PosterTile
                    key={p.user_id}
                    poster={p}
                    onRequest={() => requestFavourite(p.user_id, p.handle)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Friends strip: pinned favourites revealed avatars.
              Tapping opens the DM thread; long-press or the × icon unfriends. */}
          {friends.length > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mb-3" data-testid="friends-strip">
              <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-900 mb-2 flex items-center gap-1.5">
                <Heart className="w-3 h-3 fill-emerald-700" />
                Favourite friends · {friends.length}
              </p>
              <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                {friends.map((f) => (
                  <div key={f.id} className="flex-shrink-0 relative">
                    <button
                      onClick={() => navigate(`/dm/${f.user_id}`)}
                      className="flex flex-col items-center w-16 group"
                      data-testid={`friend-${f.id}`}
                      title={`${f.handle} · tap to open DM`}
                    >
                      <UserAvatar
                        user={{ avatar: f.avatar }}
                        size="md"
                        className="border-2 border-emerald-400 group-hover:border-emerald-600"
                      />
                      <span className="text-[10px] text-emerald-900 font-semibold mt-1 truncate w-full text-center">{f.handle}</span>
                    </button>
                    <button
                      onClick={() => unfriend(f.id, f.handle)}
                      className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-white border border-emerald-300 text-emerald-700 text-[10px] font-bold hover:bg-rose-50 hover:text-rose-700 hover:border-rose-300 flex items-center justify-center"
                      data-testid={`friend-remove-${f.id}`}
                      title="Remove from favourites"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Messages */}
          <div className="space-y-3">
            {messages.length === 0 ? (
              <div className="bg-white border border-zinc-200 rounded-xl p-6 text-center">
                <p className="text-sm text-zinc-500">No messages yet in {room || GLOBAL_ROOM}. Start the conversation.</p>
              </div>
            ) : (
              messages.map((message) => {
                const isOwnMessage = message.user_id === user?.user_id;
                const showReveal = message.is_friend && message.avatar_reveal;
                return (
                  <div
                    key={message.message_id}
                    className={`bg-white border rounded-xl p-4 ${
                      message.hidden_by_moderation
                        ? 'border-zinc-200 opacity-70'
                        : message.flagged
                          ? 'border-red-200 bg-red-50'
                          : 'border-zinc-200'
                    }`}
                    data-testid={`group-message-${message.message_id}`}
                  >
                    <div className="flex justify-between items-start mb-2 gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        {showReveal ? (
                          <UserAvatar
                            user={{ avatar: message.avatar_reveal }}
                            size="sm"
                            className="border border-emerald-400"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-zinc-100 border border-zinc-200 flex items-center justify-center flex-shrink-0">
                            <span className="text-base leading-none">{message.anon_avatar || anonAvatarFor(message.user_name)}</span>
                          </div>
                        )}
                        <span className="text-xs font-bold text-zinc-900 truncate">{message.user_name}</span>
                        {message.is_friend && !isOwnMessage && (
                          <Heart className="w-3 h-3 fill-emerald-500 text-emerald-500 flex-shrink-0" title="Favourite friend" />
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-xs text-zinc-400">
                          {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {!isOwnMessage && (
                          <button
                            onClick={() => flagMessage(message.message_id)}
                            className="text-zinc-300 hover:text-rose-500 transition-colors"
                            title="Report this message"
                            data-testid={`flag-btn-${message.message_id}`}
                          >
                            <Flag className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>

                    {message.hidden_by_moderation ? (
                      <div className="bg-zinc-100 border border-dashed border-zinc-300 rounded-md p-3 mt-1" data-testid={`hidden-message-${message.message_id}`}>
                        <p className="text-xs italic text-zinc-500">
                          This message was flagged for review and is awaiting moderation.
                        </p>
                      </div>
                    ) : message.translated_content && !isOwnMessage ? (
                      <div>
                        <p className="text-sm text-zinc-700">{message.translated_content}</p>
                        <div className="flex items-center gap-1 mt-2" data-testid="translated-indicator">
                          <Globe className="w-3 h-3 text-blue-500" />
                          <span className="text-[10px] text-blue-500 font-medium">Translated from {message.translated_from}</span>
                        </div>
                        <p className="text-[11px] text-zinc-400 italic mt-1">{message.content}</p>
                      </div>
                    ) : (
                      <p className="text-sm text-zinc-700">{message.content}</p>
                    )}

                    {message.flagged && !message.hidden_by_moderation && (
                      <p className="text-xs text-red-600 mt-2 font-medium">
                        Flagged{message.flag_reason ? ` · ${message.flag_reason}` : ' by moderator'}
                        {isOwnMessage && ' (only you and moderators can see the original)'}
                      </p>
                    )}
                  </div>
                );
              })
            )}
          </div>

          <ContentNavFooter />
        </div>
      </main>

      {/* Message Input */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-zinc-200 p-4">
        <div className="max-w-md mx-auto flex space-x-2">
          <Textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={`Share with ${room || GLOBAL_ROOM}…`}
            rows={2}
            className="flex-1 bg-white border border-zinc-300 focus:border-zinc-950 rounded-md"
            data-testid="group-message-input"
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }}}
          />
          <Button
            onClick={sendMessage}
            className="bg-zinc-950 text-white hover:bg-zinc-800 rounded-md px-4"
            data-testid="send-group-message-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Edit pseudonym modal (only when user re-opens it from header) */}
      {showHandleGate && handle && (
        <HandleGate
          onSaved={(newHandle) => { setHandle(newHandle); setShowHandleGate(false); }}
          onCancel={() => setShowHandleGate(false)}
        />
      )}
    </div>
  );
}

export default GroupChatPage;
